import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CommentEditor,
  createFragmentFormatApi,
  type FragmentEditorHandle,
  FormattingToolbar,
} from "@norkron/web-elaborate-editor";
import {
  decodeFragment,
  type DoczFragment,
  emptyFragment,
  encodeFragment,
  fragmentText,
  isEmptyFragment,
  textFragment,
} from "@norkron/web-docz";
import type {
  CommentType,
  CommentVisibility,
  WorkspaceMember,
} from "../../api/types";
import {
  AtSign,
  Building2,
  Globe,
  User,
  Users,
  X,
} from "../../compose/Icon";
import { deriveMentions } from "../../lib/mentions";
import { useDoczModule } from "../../lib/useDoczModule";

/**
 * The review-comment composer — a FIRST-CLASS RICH-TEXT editor (rich-comments
 * §2), NOT a markdown textarea. It composes the lib's `<CommentEditor>` (a
 * WYSIWYG surface over a single docz `DoczFragment` — edit===render, driven by
 * the real `docz.wasm` transform verbs) + `<FormattingToolbar mode="comment">`
 * (bold / italic / underline / strike / code / link / lists). The body is stored
 * as an encoded docz fragment (`body_format:"docz-fragment"`), NEVER markdown;
 * the discriminator lets legacy `markdown` rows keep rendering.
 *
 * The composer no longer prints the selected quote — the reviewer's selection is
 * materialized as a type-colored PENDING highlight in the paper itself (§3), so
 * the popup carries ONLY the type picker (root) + the rich body + submit/cancel.
 * Reusable for the anchored root (type picker shown) and a reply
 * (`showType={false}`).
 */

export interface ComposerDraft {
  body: string;
  body_format: string;
  type: CommentType;
  mentions: number[];
  /** comment-visibility (§6.1): the scope chosen at creation (default public). */
  visibility: CommentVisibility;
  /** The explicit audience picked when `visibility === "shared"`. */
  shared_with: number[];
}

/** Seed the editor with a STORED comment body (review-notes §3 author edit): a
 *  `docz-fragment` body decodes back to its `DoczFragment`; a legacy `markdown`
 *  body opens as a single plain-text run. A malformed fragment degrades to empty
 *  rather than throwing (the editor stays usable). */
function seedFragment(body?: string, format?: string): DoczFragment {
  if (!body) return emptyFragment();
  try {
    if (format === "docz-fragment") return decodeFragment(body);
    return textFragment(body);
  } catch {
    return emptyFragment();
  }
}

const TYPES: CommentType[] = ["comment", "suggestion", "request-for-change"];
const TYPE_LABEL: Record<CommentType, string> = {
  comment: "Comment",
  suggestion: "Suggestion",
  "request-for-change": "Request change",
};

// comment-visibility (§6.1): the four scope options for the composer picker.
// Glyph per option so color is never the only signal (WCAG 1.4.1).
const VIS_OPTIONS: {
  scope: CommentVisibility;
  label: string;
  Glyph: typeof Globe;
}[] = [
  { scope: "public", label: "Public", Glyph: Globe },
  { scope: "internal", label: "Internal", Glyph: Building2 },
  { scope: "shared", label: "Shared", Glyph: Users },
  { scope: "private", label: "Private", Glyph: User },
];

export function CommentComposer({
  members,
  onSubmit,
  onCancel,
  onTypeChange,
  showType = true,
  autoFocus = false,
  submitLabel = "Comment",
  testid = "comment-composer",
  initialBody,
  initialFormat,
  initialType,
  submitTestid = "composer-submit",
  cancelTestid = "composer-cancel",
}: {
  members: WorkspaceMember[];
  onSubmit: (draft: ComposerDraft) => Promise<void> | void;
  onCancel?: () => void;
  /** rich-comments §3.2: fired when the type pick changes, so the host can
   *  re-color the pending selection highlight in the paper to the previewed type. */
  onTypeChange?: (type: CommentType) => void;
  showType?: boolean;
  autoFocus?: boolean;
  submitLabel?: string;
  testid?: string;
  /** review-notes §3 author edit: prefill the editor with an existing body +
   *  format (decoded back into the rich fragment) so the author edits in place. */
  initialBody?: string;
  initialFormat?: string;
  /** Prefill the type picker (root edit); ignored when `showType` is false. */
  initialType?: CommentType;
  /** Override the submit/cancel testids so the edit-in-place composer can expose
   *  `comment-edit-save-{id}` / `comment-edit-cancel-{id}` (review-notes §3.7). */
  submitTestid?: string;
  cancelTestid?: string;
}) {
  const module = useDoczModule();
  const [handle, setHandle] = useState<FragmentEditorHandle | null>(null);
  const [fragment, setFragment] = useState<DoczFragment>(() =>
    seedFragment(initialBody, initialFormat),
  );
  const [type, setType] = useState<CommentType>(initialType ?? "comment");
  const [busy, setBusy] = useState(false);
  // comment-visibility (§6.1): scope + audience picked at creation (root only).
  const [visibility, setVisibility] = useState<CommentVisibility>("public");
  const [audience, setAudience] = useState<number[]>([]);
  const [audienceQuery, setAudienceQuery] = useState("");

  // The toolbar drives the SAME docz surface bold/italic/etc. use — a thin
  // control bridge over the CommentEditor handle (rich-comments §2.2 / HR13).
  const fragmentApi = useMemo(
    () => (handle && module ? createFragmentFormatApi(handle, module) : null),
    [handle, module],
  );

  // Focus the editable surface when the composer opens anchored to a selection.
  useEffect(() => {
    if (autoFocus && handle?.root) handle.root.focus();
  }, [autoFocus, handle]);

  const pickType = useCallback(
    (t: CommentType) => {
      setType(t);
      onTypeChange?.(t);
    },
    [onTypeChange],
  );

  // comment-visibility (§6.1): a `shared` note must name ≥1 person to submit.
  const sharedNeedsAudience = visibility === "shared" && audience.length === 0;

  const submit = useCallback(async () => {
    if (isEmptyFragment(fragment) || busy) return;
    if (visibility === "shared" && audience.length === 0) return;
    setBusy(true);
    try {
      await onSubmit({
        body: encodeFragment(fragment),
        body_format: "docz-fragment",
        type,
        mentions: deriveMentions(fragmentText(fragment), members),
        visibility,
        shared_with: visibility === "shared" ? audience : [],
      });
      setFragment(emptyFragment());
      setType("comment");
      setVisibility("public");
      setAudience([]);
      setAudienceQuery("");
    } finally {
      setBusy(false);
    }
  }, [fragment, busy, type, members, onSubmit, visibility, audience]);

  // The shell owns ⌘/Ctrl+Enter (submit) + Esc (cancel) WITHOUT stealing typing
  // keys — the fragment controller keeps Enter (newline) + ⌘/Ctrl+B/I/U (§2.5).
  const onKeyDownCapture = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        void submit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onCancel?.();
      }
    },
    [submit, onCancel],
  );

  const empty = isEmptyFragment(fragment);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: shell-level submit/cancel keys layered over the editable surface.
    <div className="composer" data-testid={testid} onKeyDownCapture={onKeyDownCapture}>
      {showType ? (
        <div className="composer__types" data-testid="type-picker" role="radiogroup" aria-label="Comment type">
          {TYPES.map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={type === t}
              className={`type-pick type-pick--${t}${type === t ? " type-pick--active" : ""}`}
              data-testid={`type-pick-${t}`}
              onClick={() => pickType(t)}
            >
              {TYPE_LABEL[t]}
            </button>
          ))}
        </div>
      ) : null}

      {/* comment-visibility (§6.1): the scope picker — root composer only. */}
      {showType ? (
        <div
          className="composer__visibility"
          data-testid="visibility-picker"
          role="radiogroup"
          aria-label="Who can see this note"
        >
          {VIS_OPTIONS.map((o) => (
            <button
              key={o.scope}
              type="button"
              role="radio"
              aria-checked={visibility === o.scope}
              className={`vis-pick vis-pick--${o.scope}${visibility === o.scope ? " vis-pick--active" : ""}`}
              data-testid={`visibility-opt-${o.scope}`}
              data-visibility={o.scope}
              onClick={() => setVisibility(o.scope)}
            >
              <o.Glyph size={12} strokeWidth={1.75} aria-hidden="true" />
              {o.label}
            </button>
          ))}
        </div>
      ) : null}

      {/* The audience multi-select — revealed when "Shared with people…" (§6.2). */}
      {showType && visibility === "shared" ? (
        <div className="composer__audience" data-testid="visibility-audience-picker">
          {audience.length > 0 ? (
            <div className="composer__audience-chips">
              {audience.map((uid) => {
                const m = members.find((x) => x.id === uid);
                if (!m) return null;
                return (
                  <span
                    key={uid}
                    className="composer__audience-chip"
                    data-testid={`visibility-audience-chip-${uid}`}
                  >
                    {m.name}
                    <button
                      type="button"
                      aria-label={`Remove ${m.name}`}
                      onClick={() =>
                        setAudience((a) => a.filter((x) => x !== uid))
                      }
                    >
                      <X size={11} strokeWidth={2} />
                    </button>
                  </span>
                );
              })}
            </div>
          ) : null}
          <input
            className="composer__audience-input"
            type="text"
            aria-label="Add people"
            placeholder="Add people…"
            value={audienceQuery}
            onChange={(e) => setAudienceQuery(e.target.value)}
          />
          {audienceQuery.trim() !== "" ? (
            <ul
              className="composer__audience-list"
              role="listbox"
              aria-label="Add people"
            >
              {members
                .filter(
                  (m) =>
                    !audience.includes(m.id) &&
                    (m.name
                      .toLowerCase()
                      .includes(audienceQuery.trim().toLowerCase()) ||
                      m.email
                        .toLowerCase()
                        .includes(audienceQuery.trim().toLowerCase())),
                )
                .map((m) => (
                  <li key={m.id} role="none">
                    <button
                      type="button"
                      role="option"
                      aria-selected={false}
                      className="composer__audience-item"
                      data-testid="visibility-audience-item"
                      data-user-id={m.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setAudience((a) =>
                          a.includes(m.id) ? a : [...a, m.id],
                        );
                        setAudienceQuery("");
                      }}
                    >
                      <span className="composer__audience-item-name">
                        {m.name}
                      </span>
                      <span className="composer__audience-item-email">
                        {m.email}
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
          ) : null}
          {sharedNeedsAudience ? (
            <div className="composer__audience-hint">
              Pick at least one person
            </div>
          ) : null}
        </div>
      ) : null}

      {fragmentApi ? (
        <FormattingToolbar
          api={fragmentApi}
          mode="comment"
          onCommand={() => handle?.root?.focus()}
          className="composer__toolbar"
          testId="comment-toolbar"
        />
      ) : null}

      {module ? (
        <CommentEditor
          ref={setHandle}
          module={module}
          value={fragment}
          onChange={setFragment}
          className="composer__surface"
          testId="comment-editor"
        />
      ) : (
        <div className="composer__surface composer__surface--loading" data-testid="comment-editor-loading" aria-hidden="true" />
      )}

      <div className="composer__actions">
        <span className="composer__hint">
          <AtSign size={12} strokeWidth={1.5} /> to mention · ⌘↵ to send
        </span>
        <span className="composer__actions-spacer" />
        {onCancel ? (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            data-testid={cancelTestid}
            onClick={onCancel}
          >
            Cancel
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn--primary btn--sm"
          data-testid={submitTestid}
          disabled={busy || empty || !module || sharedNeedsAudience}
          onClick={() => void submit()}
        >
          {busy ? "Saving…" : submitLabel}
        </button>
      </div>
    </div>
  );
}
